/**
 * Active Session Model
 * Represents a currently active time tracking session
 */
export class ActiveSessionModel {
  constructor({
    staffId = '',
    departmentId = '',
    clockIn = null,
    onBreak = false,
    breakStart = null,
    deviceType = 'Web',
    deviceId = 'admin',
    location = { lat: 0, lon: 0 }
  } = {}) {
    this.staffId = staffId;
    this.departmentId = departmentId;
    this.clockIn = clockIn;
    this.onBreak = onBreak;
    this.breakStart = breakStart;
    this.deviceType = deviceType;
    this.deviceId = deviceId;
    this.location = location;
  }

  // Convert to Firestore data format
  toFirestoreData() {
    return {
      departmentId: this.departmentId,
      clockIn: this.clockIn,
      onBreak: this.onBreak,
      breakStart: this.breakStart || null,
      deviceType: this.deviceType,
      deviceId: this.deviceId,
      location: this.location
    };
  }

  // Create from Firestore document
  static fromFirestore(data, staffId) {
    return new ActiveSessionModel({
      staffId,
      departmentId: data.departmentId || '',
      clockIn: data.clockIn,
      onBreak: data.onBreak || false,
      breakStart: data.breakStart || null,
      deviceType: data.deviceType || 'iOS',
      deviceId: data.deviceId || 'unknown',
      location: data.location || { lat: 0, lon: 0 }
    });
  }

  // Get session duration in seconds
  getSessionDuration() {
    if (!this.clockIn) return 0;
    const clockInTime = this.clockIn.toDate ? this.clockIn.toDate() : new Date(this.clockIn);
    return Math.floor((Date.now() - clockInTime.getTime()) / 1000);
  }

  // Get current break duration in seconds
  getCurrentBreakDuration() {
    if (!this.onBreak || !this.breakStart) return 0;
    const breakStartTime = this.breakStart.toDate ? this.breakStart.toDate() : new Date(this.breakStart);
    return Math.floor((Date.now() - breakStartTime.getTime()) / 1000);
  }

  // Validate session data
  isValid() {
    return !!(this.staffId && this.departmentId && this.clockIn);
  }
}

/**
 * Time Entry Model
 * Represents a historical time entry for a specific date
 */
export class TimeEntryModel {
  constructor({
    id = '',
    staffId = '',
    departmentId = '',
    date = '',
    clockIns = [],
    clockOuts = [],
    breakStarts = [],
    breakEnds = [],
    totalWorkDuration = 0,
    totalBreakDuration = 0,
    sessionStatus = 'Not Clocked In',
    deviceType = 'Web',
    deviceId = 'admin',
    note = '',
    createdAt = null,
    createdBy = '',
    lastModified = null,
    modifiedBy = ''
  } = {}) {
    this.id = id;
    this.staffId = staffId;
    this.departmentId = departmentId;
    this.date = date;
    this.clockIns = clockIns;
    this.clockOuts = clockOuts;
    this.breakStarts = breakStarts;
    this.breakEnds = breakEnds;
    this.totalWorkDuration = totalWorkDuration;
    this.totalBreakDuration = totalBreakDuration;
    this.sessionStatus = sessionStatus;
    this.deviceType = deviceType;
    this.deviceId = deviceId;
    this.note = note;
    this.createdAt = createdAt;
    this.createdBy = createdBy;
    this.lastModified = lastModified;
    this.modifiedBy = modifiedBy;
  }

  // Session status enum
  static SessionStatus = {
    NOT_CLOCKED_IN: 'Not Clocked In',
    CLOCKED_IN: 'Clocked In',
    WORKING: 'Working',
    ON_BREAK: 'On Break',
    CLOCKED_OUT: 'Clocked Out'
  };

  // Entry creation types
  static CreationType = {
    AUTOMATIC: 'Automatic', // Created by iOS app automatically
    MANUAL_ADMIN: 'Manual Admin', // Manually created by admin
    IMPORT: 'Import', // Imported from external system
    SYSTEM_GENERATED: 'System Generated' // Generated by system processes
  };

  // Convert to Firestore data format
  toFirestoreData() {
    const data = {
      departmentId: this.departmentId,
      date: this.date,
      clockIns: this.clockIns,
      clockOuts: this.clockOuts,
      breakStarts: this.breakStarts,
      breakEnds: this.breakEnds,
      totalWorkDuration: this.totalWorkDuration,
      totalBreakDuration: this.totalBreakDuration,
      sessionStatus: this.sessionStatus,
      deviceType: this.deviceType,
      deviceId: this.deviceId,
      note: this.note
    };

    // Include audit fields if they exist
    if (this.createdAt) data.createdAt = this.createdAt;
    if (this.createdBy) data.createdBy = this.createdBy;
    if (this.lastModified) data.lastModified = this.lastModified;
    if (this.modifiedBy) data.modifiedBy = this.modifiedBy;

    return data;
  }

  // Create from Firestore document
  static fromFirestore(data, id, staffId) {
    return new TimeEntryModel({
      id,
      staffId,
      departmentId: data.departmentId || '',
      date: data.date || '',
      clockIns: data.clockIns || [],
      clockOuts: data.clockOuts || [],
      breakStarts: data.breakStarts || [],
      breakEnds: data.breakEnds || [],
      totalWorkDuration: data.totalWorkDuration || 0,
      totalBreakDuration: data.totalBreakDuration || 0,
      sessionStatus: data.sessionStatus || TimeEntryModel.SessionStatus.NOT_CLOCKED_IN,
      deviceType: data.deviceType || 'iOS',
      deviceId: data.deviceId || 'unknown',
      note: data.note || '',
      createdAt: data.createdAt || null,
      createdBy: data.createdBy || '',
      lastModified: data.lastModified || null,
      modifiedBy: data.modifiedBy || ''
    });
  }

  // Create a new manual time entry template
  static createManualTemplate(staffId, departmentId, date) {
    const currentDate = new Date(date);
    const dateString = date; // Should be in YYYY-MM-DD format
    
    // Set default times: 9:00 AM - 5:00 PM
    const clockInTime = new Date(currentDate);
    clockInTime.setHours(9, 0, 0, 0);
    
    const clockOutTime = new Date(currentDate);
    clockOutTime.setHours(17, 0, 0, 0);
    
    return new TimeEntryModel({
      id: '', // Will be set when saved to Firestore
      staffId,
      departmentId,
      date: dateString,
      clockIns: [clockInTime],
      clockOuts: [clockOutTime],
      breakStarts: [],
      breakEnds: [],
      totalWorkDuration: 8 * 60 * 60, // 8 hours in seconds
      totalBreakDuration: 0,
      sessionStatus: TimeEntryModel.SessionStatus.CLOCKED_OUT,
      deviceType: 'Web',
      deviceId: 'admin',
      note: '',
      createdAt: new Date(),
      createdBy: 'admin'
    });
  }

  // Get formatted date string
  getFormattedDate() {
    const dateObj = new Date(this.date);
    return dateObj.toLocaleDateString();
  }

  // Get last clock in time
  getLastClockIn() {
    return this.clockIns.length > 0 ? this.clockIns[this.clockIns.length - 1] : null;
  }

  // Get last clock out time
  getLastClockOut() {
    return this.clockOuts.length > 0 ? this.clockOuts[this.clockOuts.length - 1] : null;
  }

  // Check if currently has active session
  hasActiveSession() {
    return this.clockIns.length > this.clockOuts.length;
  }

  // Get total sessions count
  getSessionsCount() {
    return Math.max(this.clockIns.length, this.clockOuts.length);
  }

  // Check if this entry was manually created
  isManuallyCreated() {
    return this.deviceType === 'Web' && this.deviceId === 'admin';
  }

  // Get creation method description
  getCreationMethod() {
    if (this.deviceType === 'Web' && this.deviceId === 'admin') {
      return 'Manually created by admin';
    } else if (this.deviceType === 'iOS') {
      return 'Automatically created by mobile app';
    } else {
      return 'Unknown creation method';
    }
  }

  // Validate timestamp arrays
  validateTimestamps() {
    const errors = [];

    // Check that we don't have more clock outs than clock ins
    if (this.clockOuts.length > this.clockIns.length) {
      errors.push('Cannot have more clock outs than clock ins');
    }

    // Check that we don't have more break ends than break starts
    if (this.breakEnds.length > this.breakStarts.length) {
      errors.push('Cannot have more break ends than break starts');
    }

    // Validate timestamp order within each array
    const validateArray = (arr, name) => {
      for (let i = 1; i < arr.length; i++) {
        const prev = arr[i - 1].toDate ? arr[i - 1].toDate() : new Date(arr[i - 1]);
        const curr = arr[i].toDate ? arr[i].toDate() : new Date(arr[i]);
        if (curr <= prev) {
          errors.push(`${name} timestamps must be in chronological order`);
          break;
        }
      }
    };

    validateArray(this.clockIns, 'Clock in');
    validateArray(this.clockOuts, 'Clock out');
    validateArray(this.breakStarts, 'Break start');
    validateArray(this.breakEnds, 'Break end');

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Validate business rules for time entry
  validateBusinessRules() {
    const errors = [];

    // Must have at least one clock in
    if (this.clockIns.length === 0) {
      errors.push('Time entry must have at least one clock in time');
    }

    // Validate date format
    if (!this.date || !this.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      errors.push('Date must be in YYYY-MM-DD format');
    }

    // Validate department
    if (!this.departmentId) {
      errors.push('Department is required');
    }

    // Validate that breaks are within work sessions
    const clockIns = this.clockIns.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
    const clockOuts = this.clockOuts.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
    const breakStarts = this.breakStarts.map(ts => ts.toDate ? ts.toDate() : new Date(ts));
    const breakEnds = this.breakEnds.map(ts => ts.toDate ? ts.toDate() : new Date(ts));

    for (let i = 0; i < breakStarts.length; i++) {
      let breakInWorkSession = false;
      for (let j = 0; j < Math.min(clockIns.length, clockOuts.length); j++) {
        if (breakStarts[i] >= clockIns[j] && 
            (i < breakEnds.length && breakEnds[i] <= clockOuts[j])) {
          breakInWorkSession = true;
          break;
        }
      }
      if (!breakInWorkSession && clockIns.length > 0 && clockOuts.length > 0) {
        errors.push(`Break ${i + 1} must occur within a work session`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Check if entry is complete (all sessions closed)
  isComplete() {
    return this.clockIns.length === this.clockOuts.length && 
           this.breakStarts.length === this.breakEnds.length;
  }
}

/**
 * Time Adjustment Model
 * Represents an admin adjustment to time entries
 */
export class TimeAdjustmentModel {
  constructor({
    id = '',
    companyId = '',
    staffId = '',
    entryId = '',
    adjustedBy = '',
    adjustmentDate = new Date(),
    originalData = {},
    newData = {},
    reason = '',
    adjustmentType = 'MANUAL_EDIT'
  } = {}) {
    this.id = id;
    this.companyId = companyId;
    this.staffId = staffId;
    this.entryId = entryId;
    this.adjustedBy = adjustedBy;
    this.adjustmentDate = adjustmentDate;
    this.originalData = originalData;
    this.newData = newData;
    this.reason = reason;
    this.adjustmentType = adjustmentType;
  }

  // Adjustment types
  static AdjustmentType = {
    MANUAL_EDIT: 'Manual Edit',
    MANUAL_CREATION: 'Manual Creation', // New type for manually created entries
    ADMIN_CLOCK_OUT: 'Admin Clock Out',
    TIME_CORRECTION: 'Time Correction',
    SYSTEM_FIX: 'System Fix',
    BULK_IMPORT: 'Bulk Import'
  };

  // Convert to Firestore data format
  toFirestoreData() {
    return {
      companyId: this.companyId,
      staffId: this.staffId,
      entryId: this.entryId,
      adjustedBy: this.adjustedBy,
      adjustmentDate: this.adjustmentDate,
      originalData: this.originalData,
      newData: this.newData,
      reason: this.reason,
      adjustmentType: this.adjustmentType
    };
  }

  // Create from Firestore document
  static fromFirestore(data, id) {
    return new TimeAdjustmentModel({
      id,
      companyId: data.companyId || '',
      staffId: data.staffId || '',
      entryId: data.entryId || '',
      adjustedBy: data.adjustedBy || '',
      adjustmentDate: data.adjustmentDate?.toDate() || new Date(),
      originalData: data.originalData || {},
      newData: data.newData || {},
      reason: data.reason || '',
      adjustmentType: data.adjustmentType || TimeAdjustmentModel.AdjustmentType.MANUAL_EDIT
    });
  }

  // Create adjustment record for manual entry creation
  static createForManualEntry(companyId, staffId, entryId, entryData, reason, adjustedBy = 'admin') {
    return new TimeAdjustmentModel({
      companyId,
      staffId,
      entryId,
      adjustedBy,
      adjustmentDate: new Date(),
      originalData: {}, // No original data for new entries
      newData: entryData,
      reason: reason || 'Manually created time entry',
      adjustmentType: TimeAdjustmentModel.AdjustmentType.MANUAL_CREATION
    });
  }

  // Create adjustment record for entry edit
  static createForEdit(companyId, staffId, entryId, originalData, newData, reason, adjustedBy = 'admin') {
    return new TimeAdjustmentModel({
      companyId,
      staffId,
      entryId,
      adjustedBy,
      adjustmentDate: new Date(),
      originalData,
      newData,
      reason: reason || 'Time entry manually edited',
      adjustmentType: TimeAdjustmentModel.AdjustmentType.MANUAL_EDIT
    });
  }

  // Get formatted adjustment date
  getFormattedDate() {
    return this.adjustmentDate.toLocaleString();
  }

  // Get adjustment summary description
  getAdjustmentSummary() {
    switch (this.adjustmentType) {
      case TimeAdjustmentModel.AdjustmentType.MANUAL_CREATION:
        return `Manually created new time entry`;
      case TimeAdjustmentModel.AdjustmentType.MANUAL_EDIT:
        return `Manually edited existing time entry`;
      case TimeAdjustmentModel.AdjustmentType.ADMIN_CLOCK_OUT:
        return `Admin clock out performed`;
      case TimeAdjustmentModel.AdjustmentType.TIME_CORRECTION:
        return `Time correction applied`;
      case TimeAdjustmentModel.AdjustmentType.SYSTEM_FIX:
        return `System fix applied`;
      default:
        return `Time entry adjustment`;
    }
  }
}

/**
 * Staff Session Summary Model
 * Aggregated view of staff time data
 */
export class StaffSessionSummaryModel {
  constructor({
    staffId = '',
    staffName = '',
    departmentId = '',
    departmentName = '',
    activeSession = null,
    todayEntry = null,
    weeklyHours = 0,
    monthlyHours = 0
  } = {}) {
    this.staffId = staffId;
    this.staffName = staffName;
    this.departmentId = departmentId;
    this.departmentName = departmentName;
    this.activeSession = activeSession;
    this.todayEntry = todayEntry;
    this.weeklyHours = weeklyHours;
    this.monthlyHours = monthlyHours;
  }

  // Check if staff is currently active
  isCurrentlyActive() {
    return this.activeSession !== null;
  }

  // Check if staff is on break
  isOnBreak() {
    return this.activeSession?.onBreak || false;
  }

  // Get current session duration
  getCurrentSessionDuration() {
    return this.activeSession?.getSessionDuration() || 0;
  }

  // Get today's total work duration
  getTodayWorkDuration() {
    return this.todayEntry?.totalWorkDuration || 0;
  }

  // Get today's total break duration
  getTodayBreakDuration() {
    return this.todayEntry?.totalBreakDuration || 0;
  }
}